import { Button, Form, Input, InputNumber, Popconfirm, Table, Typography } from 'antd'
import React, { useEffect, useState } from 'react'

interface Item {
  key: string
  bankReference: string
  age: number
  address: string
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean
  dataIndex: string
  title: any
  inputType: 'number' | 'text'
  record: Item
  index: number
  children: React.ReactNode
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === 'number' ? <InputNumber /> : <Input />

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  )
}

export const EditableTable = ({ holdings }) => {
  const [editableCellForm] = Form.useForm()
  const [data, setData] = useState(holdings)
  const [editingKey, setEditingKey] = useState('')

  useEffect(() => {
    setData(holdings)
  }, [holdings])

  const isEditing = (record: Item) => record.key === editingKey

  const edit = (record: Partial<Item> & { key: React.Key }) => {
    editableCellForm.setFieldsValue(record)
    setEditingKey(record.key)
  }

  const cancel = () => {
    setEditingKey('')
  }

  const save = async (key: React.Key) => {
    try {
      const row = (await editableCellForm.validateFields()) as Item

      const newData = [...data]
      const index = newData.findIndex((item) => key === item.key)
      if (index > -1) {
        const item = newData[index]
        newData.splice(index, 1, { ...item, ...row })
        setData(newData)
        setEditingKey('')
      } else {
        newData.push(row)
        setData(newData)
        setEditingKey('')
      }
    } catch (errInfo) {}
  }

  const columns = [
    {
      title: 'Purchase date',
      dataIndex: 'purchaseDate',
      editable: true,
    },
    {
      title: 'Maturity date',
      dataIndex: 'maturityDate',
      editable: true,
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      editable: true,
    },
    {
      title: 'Coupon',
      dataIndex: 'coupon',
      editable: true,
    },
    {
      title: 'Instrument code',
      dataIndex: 'instrumentCode',
      editable: true,
    },
    {
      title: 'Interest paid code',
      dataIndex: 'interestPaidCode',
      editable: true,
    },
    {
      title: 'Bank reference',
      dataIndex: 'bankReference',
      editable: true,
    },
    {
      title: 'Allocation code',
      dataIndex: 'allocationCode',
      editable: true,
      render: (allocationCode: string) => String(allocationCode).toLocaleUpperCase(),
    },
    {
      title: 'Operation',
      dataIndex: 'operation',
      render: (_: any, record: Item) => {
        const editable = isEditing(record)
        return editable ? (
          <span>
            <Button type="link" onClick={() => save(record.key)} style={{ marginRight: 8 }}>
              Save
            </Button>
            <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
              Cancel
            </Popconfirm>
          </span>
        ) : (
          <Typography.Link disabled={editingKey !== ''} onClick={() => edit(record)}>
            Edit
          </Typography.Link>
        )
      },
    },
  ]

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex === 'age' ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    }
  })

  return (
    <Form form={editableCellForm} component={false}>
      <Table
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        size="small"
        bordered
        dataSource={data}
        columns={mergedColumns}
        className="editable-trades-table"
        rowClassName="editable-row"
        pagination={false}
      />
    </Form>
  )
}
